import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Repeat, X, ChevronDown, Pause, Play, Square, Trash2 } from "lucide-react";

const HEALTH_STYLES = {
  on_track: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  at_risk: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  over: "text-rose-400 bg-rose-500/10 border-rose-500/20",
};

const HEALTH_BAR = { on_track: "#00F5D4", at_risk: "#f59e0b", over: "#f43f5e" };

const STATUS_STYLES = {
  active: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  paused: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  ended: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
};

function fmt(n, ccy = "GBP") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
  } catch {
    return `${ccy} ${(Number(n) || 0).toFixed(2)}`;
  }
}

const EMPTY_FORM = {
  project_id: "", name: "", client_name: "", retainer_type: "hours", period: "monthly",
  allocated_hours: "", allocated_amount: "", hourly_rate: "", currency: "GBP",
  start_date: new Date().toISOString().slice(0, 10), rollover_unused: false, notes: "",
};

export default function Retainers() {
  const [retainers, setRetainers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        api.get("/retainers"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setRetainers(rRes.data || []);
      setProjects(pRes.data || []);
    } catch {
      toast.error("Failed to load retainers");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/retainers/${r.retainer_id}?periods=6`);
      setDetail(data);
    } catch {
      toast.error("Failed to load retainer");
    }
  };

  const projName = (id) => projects.find(p => p.project_id === id)?.name || id;

  const create = async () => {
    if (!form.project_id || !form.name.trim() || !form.start_date) return toast.error("Project, name and start date are required");
    setSaving(true);
    try {
      await api.post("/retainers", {
        ...form,
        allocated_hours: parseFloat(form.allocated_hours) || 0,
        allocated_amount: parseFloat(form.allocated_amount) || 0,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      });
      toast.success("Retainer created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create retainer");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/retainers/${detail.retainer_id}`, { status });
      toast.success(`Retainer ${status}`);
      const { data } = await api.get(`/retainers/${detail.retainer_id}?periods=6`);
      setDetail(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update retainer");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete retainer "${detail.name}"? This does not delete any time logged.`)) return;
    try {
      await api.delete(`/retainers/${detail.retainer_id}`);
      toast.success("Retainer deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete retainer");
    }
  };

  if (detail) {
    const isHours = detail.retainer_type === "hours";
    const cp = detail.current_period;
    return (
      <DarkPage><div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => setDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{detail.name}</h1>
            <p className="text-sm text-[#7d8590] mt-1">
              {projName(detail.project_id)}{detail.client_name ? ` · ${detail.client_name}` : ""} · {detail.period} · {isHours ? "hours" : "fixed amount"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-3 py-1.5 rounded-full border ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>
            {detail.status === "active" && (
              <button onClick={() => setStatus("paused")} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
                <Pause className="w-3 h-3" /> Pause
              </button>
            )}
            {detail.status === "paused" && (
              <button onClick={() => setStatus("active")} className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10 flex items-center gap-1">
                <Play className="w-3 h-3" /> Resume
              </button>
            )}
            {detail.status !== "ended" && (
              <button onClick={() => setStatus("ended")} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
                <Square className="w-3 h-3" /> End
              </button>
            )}
            <button onClick={remove} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        </div>

        {/* Current period */}
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-[#7d8590]">Current period · {cp.period_start} to {cp.period_end}</div>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${HEALTH_STYLES[cp.health]}`}>{cp.health.replace("_", " ")}</span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-bold text-white">
              {isHours ? `${cp.consumed}h` : fmt(cp.consumed, detail.currency)}
            </span>
            <span className="text-sm text-[#7d8590]">
              of {isHours ? `${cp.allocated}h` : fmt(cp.allocated, detail.currency)} allocated
            </span>
          </div>
          <div className="w-full h-2 bg-[#1c2128] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, cp.percent_used)}%`, background: HEALTH_BAR[cp.health] }} />
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
            <div><div className="text-[#7d8590] text-xs mb-0.5">Remaining</div><div className={cp.remaining < 0 ? "text-rose-400 font-medium" : "text-white font-medium"}>{isHours ? `${cp.remaining}h` : fmt(cp.remaining, detail.currency)}</div></div>
            <div><div className="text-[#7d8590] text-xs mb-0.5">% used</div><div className="text-white font-medium">{cp.percent_used}%</div></div>
            <div><div className="text-[#7d8590] text-xs mb-0.5">Billable value delivered</div><div className="text-white font-medium">{fmt(cp.value_consumed, detail.currency)}</div></div>
          </div>
        </div>

        {/* Period history */}
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-[#21262d] text-xs text-[#7d8590] font-medium">Period history</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Period", "Consumed", "Allocated", "Remaining", "Health"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...detail.periods].reverse().map(p => (
                <tr key={p.period_index} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-2.5 text-[#7d8590] font-mono text-xs">{p.period_start} – {p.period_end}</td>
                  <td className="px-4 py-2.5 text-white">{isHours ? `${p.consumed}h` : fmt(p.consumed, detail.currency)}</td>
                  <td className="px-4 py-2.5 text-[#7d8590]">{isHours ? `${p.allocated}h` : fmt(p.allocated, detail.currency)}</td>
                  <td className={`px-4 py-2.5 ${p.remaining < 0 ? "text-rose-400" : "text-[#7d8590]"}`}>{isHours ? `${p.remaining}h` : fmt(p.remaining, detail.currency)}</td>
                  <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${HEALTH_STYLES[p.health]}`}>{p.health.replace("_", " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {detail.notes && (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
            <div className="text-xs text-[#7d8590] mb-1">Notes</div>
            <p className="text-sm text-white whitespace-pre-wrap">{detail.notes}</p>
          </div>
        )}
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Retainers</h1>
          <p className="text-sm text-[#7d8590] mt-1">Pre-paid hours or budget, drawn down automatically as work is logged</p>
        </div>
        <Button onClick={() => { if (projects.length === 0) { toast.error("Create a project first"); return; } setForm(EMPTY_FORM); setShowModal(true); }}
          className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Retainer
        </Button>
      </div>

      {retainers.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No retainers yet. Set one up against a project to track a client's pre-paid hours or budget each period.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {retainers.map(r => {
            const cp = r.current_period;
            const isHours = r.retainer_type === "hours";
            return (
              <div key={r.retainer_id} onClick={() => openDetail(r)}
                className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 cursor-pointer hover:border-[#00F5D4]/30 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-white font-semibold flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5 text-[#7d8590]" /> {r.name}</div>
                    <div className="text-xs text-[#7d8590] mt-0.5">{projName(r.project_id)}{r.client_name ? ` · ${r.client_name}` : ""}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                </div>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-lg font-bold text-white">{isHours ? `${cp.consumed}h` : fmt(cp.consumed, r.currency)}</span>
                  <span className="text-xs text-[#7d8590]">of {isHours ? `${cp.allocated}h` : fmt(cp.allocated, r.currency)} this {r.period.replace("ly", "")}</span>
                </div>
                <div className="w-full h-1.5 bg-[#1c2128] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, cp.percent_used)}%`, background: HEALTH_BAR[cp.health] }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New retainer modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Retainer</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Project *</label>
                  <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="">Select project…</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Client name</label>
                  <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Monthly support retainer"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Type</label>
                  <select value={form.retainer_type} onChange={e => setForm(f => ({ ...f, retainer_type: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="hours">Hours block</option>
                    <option value="fixed_amount">Fixed amount</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Period</label>
                  <select value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {form.retainer_type === "hours" ? (
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Allocated hours / period</label>
                    <input type="number" step="0.5" value={form.allocated_hours} onChange={e => setForm(f => ({ ...f, allocated_hours: e.target.value }))}
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                  </div>
                ) : (
                  <div>
                    <label className="text-xs text-[#7d8590] mb-1 block">Allocated amount / period</label>
                    <input type="number" step="0.01" value={form.allocated_amount} onChange={e => setForm(f => ({ ...f, allocated_amount: e.target.value }))}
                      className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                  </div>
                )}
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Equivalent hourly rate</label>
                  <input type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: e.target.value }))}
                    placeholder="For overage billing"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP", "USD", "EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Start date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={form.rollover_unused} onChange={e => setForm(f => ({ ...f, rollover_unused: e.target.checked }))} />
                Roll over unused hours/budget into the next period
              </label>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create retainer"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
